import { createContext, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { get } from 'aws-amplify/api';

type AuthUser = {
    username: string;
};

type License = 'paid' | 'free' | null;

type User = {
    subID: string;
    license: string;
};

type AuthContextType = {
    user: User | null;
    license: License;
    isLoading: boolean;
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [license, setLicense] = useState<License>(null);
    const [isLoading, setLoading] = useState(true);

    const fetchAttr = async (authUser: AuthUser) => {
        try {
            const { idToken } = (await fetchAuthSession()).tokens ?? {};
            const authToken = idToken?.toString() ?? '';
            const { body } = await get({
                apiName: 'KMGCloud',
                path: '/mitarbeiter',
                options: {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: authToken?.toString() ?? '',
                    },
                    queryParams: {
                        sub: authUser.username,
                    },
                },
            }).response;
            const users = (await body.json()) as any;
            const data = users.find((item:any) => item.subID === authUser.username);
            if (data) {
                setUser(data);
                setLicense(data.licence ? 'paid' : 'free');
            } else {
                setUser(null);
                setLicense('free');
            }
        } catch (err) {
            console.log(err);
            setLicense('free');
        }
    };

    const checkUser = async () => {
        try {
            await getCurrentUser().then(async (authUser: AuthUser) => {
                await fetchAttr(authUser);
            });
        } catch {
            setUser(null);
            setLicense(null);
        }
        setLoading(false);
    };

    useEffect(() => {
        checkUser();

        const unsubscribe = Hub.listen('auth', () => {
            checkUser();
        });

        return () => unsubscribe();
    }, []);

    return (
        <AuthContext.Provider value={{ user, license, isLoading }}>
            {children}
        </AuthContext.Provider>
    );
};
